import { Fragment, useEffect, useRef, useState } from "react";
import cn from "classnames";

// internal data providers
import { useAssetLibraryProvider } from "@/state/providers/AssetLibraryProvider";
import { AddCallerIdResponse, PhoneNumber, usePhoneCall } from "@/state/hooks/usePhoneCall";
import { useAppDispatch } from "@/common/hooks/useRedux";
import { deletePatientRecordsAsync } from "@/state/redux/patientTableRecordsSlice";
import { useRealtimeContextApi } from "@/state/providers/RealtimeProvider";

// internal components
import CallerIdNumberForm from "./CallerIdNumberForm";
import CallerIdVerify from "./CallerIdVerify";
import DestinationNumberForm from "./DestinationNumberForm";
import Dialer from "./Dialer";
import { Transition, TransitionChild } from "@headlessui/react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { voipAnalytics } from "@/common/utils/voipAnalytics";
import { BroadcastEvents } from "@/common/utils/types";

/**
 * Phone call widget state union type
 */
export type PhoneCallWidgetState = "enter-caller-id" | "verify-caller-id" | "enter-destination" | "dialer";

const PhoneCallWidget = () => {
    const [phoneCallWidgetState, setPhoneCallWidgetState] = useState<PhoneCallWidgetState>("enter-caller-id");
    const [verificationData, setVerificationData] = useState<AddCallerIdResponse | null>(null);
    // Whether the caller-ID form is being shown to RETRY an existing row rather than add a new one.
    // Explicit rather than derived from `verificationData` being set: the number field is locked on
    // retry, and inferring that from prefill would silently lock the Add form the moment some future
    // path reached it without clearing `verificationData` first.
    const [isCallerIdRetry, setIsCallerIdRetry] = useState(false);
    // Whether the Dialer's Client numbers picker row is showing (only with more than one client number).
    const [clientNumbersVisible, setClientNumbersVisible] = useState(false);
    const hasRenderedWithPhoneNumbersRef = useRef<boolean>(false);

    // Global state from provider
    const {
        phoneCallWidgetControls: { renderPhoneCallWidget, setRenderPhoneCallWidget, callTarget },
    } = useAssetLibraryProvider();
    const dispatch = useAppDispatch();
    const { emitEvent } = useRealtimeContextApi();

    // Server state
    const {
        phoneNumbersQuery: { data: phoneNumbers, status: phoneNumbersQueryStatus },
        verifiedCallerIds,
        staffDestinationNumbers,
    } = usePhoneCall();

    // The Dialer unmounts when the widget leaves the dialer state or closes, so clear the flag then.
    useEffect(() => {
        if (!renderPhoneCallWidget || phoneCallWidgetState !== "dialer") {
            setClientNumbersVisible(false);
        }
    }, [renderPhoneCallWidget, phoneCallWidgetState]);

    // Determines which state to start the widget on based on whether caller-id and destination phone
    // numbers are available. Only runs when opening (widget visible); do not run when closing to avoid flashing Dialer.
    useEffect(() => {
        if (!renderPhoneCallWidget) {
            return;
        }
        const isAlreadyRenderedWithPhoneNumbers = hasRenderedWithPhoneNumbersRef.current;
        hasRenderedWithPhoneNumbersRef.current = phoneNumbersQueryStatus === "success";

        // Only proceed if transitioning to rendered with phone numbers
        if (isAlreadyRenderedWithPhoneNumbers || !phoneNumbers) {
            return;
        }

        const callerIds = phoneNumbers.filter((phoneNumber) => phoneNumber.type === "caller_id");
        if (callerIds.length === 0) {
            setPhoneCallWidgetState("enter-caller-id");
        } else {
            if (verifiedCallerIds.length > 0) {
                if (staffDestinationNumbers.length === 0) {
                    setPhoneCallWidgetState("enter-destination");
                } else {
                    setPhoneCallWidgetState("dialer");
                }
            } else {
                setPhoneCallWidgetState("enter-caller-id");
            }
        }
    }, [renderPhoneCallWidget, phoneNumbers, phoneNumbersQueryStatus, verifiedCallerIds, staffDestinationNumbers]);

    // ------------------------------
    // STATE TRANSITION HANDLERS

    const handleCancel = () => {
        // Dialer only shows its own Cancel button while dialerState is "idle" (before a call
        // is confirmed, or after one fails), so reaching this handler always means no call was
        // ever placed against callTarget — safe to archive a quick-start placeholder here.
        if (callTarget?.isQuickStart && callTarget.patientRecordId) {
            const { patientRecordId } = callTarget;
            // Fire-and-forget: don't block the widget close on the archive request. Only
            // broadcast the deletion once it's confirmed, so other clients aren't told a
            // record was deleted when the request actually failed.
            dispatch(deletePatientRecordsAsync({ ids: [patientRecordId] }))
                .unwrap()
                .then((result) => {
                    if (result.deletedIds.includes(patientRecordId)) {
                        return emitEvent(BroadcastEvents.patientDeleted, { patientRecordId });
                    }
                    if (result.notFoundIds.includes(patientRecordId)) {
                        // EN-1088: the placeholder isn't there at all - already cleaned up
                        // elsewhere, or never visible to this user. The cleanup's goal is met, so
                        // stay silent: no broadcast (this client deleted nothing) and no error.
                        // Claiming "a recording is in progress" here would invent one.
                        return undefined;
                    }
                    if (result.erroredIds.includes(patientRecordId)) {
                        toast.error("Failed to remove placeholder patient record");
                    } else {
                        // EN-1088: a live recording lease refused the archive (HTTP 200, empty
                        // array - not a Supabase error, so we land here even though .unwrap()
                        // didn't reject). The placeholder is still alive with an active
                        // recording, so nothing was broadcast above. The widget has already
                        // closed by this point regardless, so there is nothing further to unwind.
                        toast.error("Can't remove this placeholder - a recording is in progress.");
                    }
                    return undefined;
                })
                .catch((error) => {
                    // Delete failed — don't broadcast a deletion that didn't happen.
                    Sentry.captureException(error);
                    toast.error("Failed to remove placeholder patient record");
                });
        }
        // callTarget is cleared centrally by AssetLibraryProvider whenever renderPhoneCallWidget
        // goes false — this handler only needs to trigger the close.
        setRenderPhoneCallWidget(false);
        setVerificationData(null);
        setIsCallerIdRetry(false);
        // Clear ref so the useEffect runs on next open and sets initial state (dialer / enter-caller-id / etc.).
        hasRenderedWithPhoneNumbersRef.current = false;
        // Reset state to dialer after leave transition (in afterLeave) to avoid flashing Dialer before close.
    };

    const handleAddCallerIdSaved = (result: AddCallerIdResponse) => {
        const status = result.verificationStatus === "success" ? "success" : "pending";
        voipAnalytics.callerIdSubmitted({ verification_status: status, source: "patient_header" });
        if (result.verificationStatus === "success") {
            voipAnalytics.callerIdVerified({ source: "patient_header" });
            toast.success("Caller ID verified");
            if (staffDestinationNumbers.length > 0) {
                setPhoneCallWidgetState("dialer");
            } else {
                setPhoneCallWidgetState("enter-destination");
            }
        } else {
            setVerificationData(result);
            setPhoneCallWidgetState("verify-caller-id");
        }
    };

    const handleCallerIdVerifiedContinue = async () => {
        voipAnalytics.callerIdVerified({ source: "patient_header" });
        setVerificationData(null);
        if (staffDestinationNumbers.length > 0) {
            setPhoneCallWidgetState("dialer");
        } else {
            setPhoneCallWidgetState("enter-destination");
        }
    };

    const handleDestinationSaved = async (_destination: PhoneNumber) => {
        voipAnalytics.destinationAdded({ source: "patient_header" });
        setPhoneCallWidgetState("dialer");
    };

    const handleAddCallerId = () => {
        setIsCallerIdRetry(false);
        setPhoneCallWidgetState("enter-caller-id");
    };

    const handleAddDestination = () => {
        setPhoneCallWidgetState("enter-destination");
    };

    const handleCallerIdRetry = () => {
        // Keep verificationData so the form can pre-fill from it. The stale verification status for
        // this row is cleared centrally by addCallerIdMutation.onSuccess when the next attempt
        // lands (EN-1051) — not here: Cancel and "Add another" reach a new attempt without ever
        // passing through this handler, and clearing per-boundary left those paths broken.
        setIsCallerIdRetry(true);
        setPhoneCallWidgetState("enter-caller-id");
    };

    // ------------------------------
    // VIEWS AND RENDERING

    // render component state based on widget state
    const getWidgetView = () => {
        switch (phoneCallWidgetState) {
            case "enter-caller-id":
                return (
                    <CallerIdNumberForm
                        onCancel={handleCancel}
                        onSave={handleAddCallerIdSaved}
                        initialPhoneNumber={verificationData?.phoneNumber}
                        initialReferenceName={verificationData?.referenceName}
                        initialExtension={verificationData?.extension ?? undefined}
                        initialDialSteps={verificationData?.dialSteps ?? undefined}
                        isRetry={isCallerIdRetry}
                    />
                );
            case "verify-caller-id":
                if (!verificationData) {
                    setIsCallerIdRetry(false);
                    setPhoneCallWidgetState("enter-caller-id");
                    return null;
                }
                return (
                    <CallerIdVerify
                        verificationData={verificationData}
                        onCancel={handleCancel}
                        onVerifiedContinue={handleCallerIdVerifiedContinue}
                        onRetry={handleCallerIdRetry}
                    />
                );
            case "enter-destination":
                return <DestinationNumberForm onCancel={handleCancel} onSave={handleDestinationSaved} />;
            case "dialer":
            default:
                return (
                    <Dialer
                        onCancel={handleCancel}
                        onAddCallerId={handleAddCallerId}
                        onAddDestination={handleAddDestination}
                        onClientNumbersVisibleChange={setClientNumbersVisible}
                    />
                );
        }
    };

    return (
        <Transition show={renderPhoneCallWidget} as={Fragment} afterLeave={() => setPhoneCallWidgetState("dialer")}>
            <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
                <div
                    className={cn(
                        "w-[270px] right-[70px] sm:w-[360px] sm:right-24 fixed bottom-5 max-h-[calc(100dvh-2.5rem)] flex flex-col rounded-lg z-50 transition-all duration-500 ease-in-out overflow-hidden",
                        {
                            "h-[500px]":
                                phoneCallWidgetState === "enter-caller-id" ||
                                phoneCallWidgetState === "enter-destination",
                            "h-[388px]": phoneCallWidgetState === "verify-caller-id",
                            "h-[690px]": phoneCallWidgetState === "dialer" && !clientNumbersVisible,
                            // Client numbers picker adds 66px to the dialer's flex column:
                            //   button: py-2 (8+8) + two text-sm lines (20px line-height each) + gap-0.5 (2px) = 58px
                            //   (no border; the chevron is 20px, shorter than the text stack)
                            //   Popover margins: -mt-2 (-8px) + mb-4 (+16px) = +8px
                            //   58 + 8 = 66px, so 690 + 66 = 756px keeps the footer's bottom spacing unchanged.
                            "h-[756px]": phoneCallWidgetState === "dialer" && clientNumbersVisible,
                        },
                    )}
                >
                    {getWidgetView()}
                </div>
            </TransitionChild>
        </Transition>
    );
};

export default PhoneCallWidget;
